import React from 'react';
import ShipmentsTileComponentV2 from './ShipmentsTileComponent.v2';
import { Icon } from '../../common/commonModels';
// tslint:disable-next-line:import-name
import ContainerDimensions from 'react-container-dimensions';
import Link from '../../common/Link';
import momentTimezone from 'moment-timezone';
import { PalletBox, PalletBoxStatus } from '../../models/pallet-box';
import { InternalShipmentGroup } from '../../models/shipment-group';
import { FlexBox, AlignItems, FlexWrap } from '../../utils/css-classes';

const tileDisplayWidth = 110;

const palletBoxCompareByStatus = (palletBoxA: PalletBox, palletBoxB: PalletBox) => {
  // Open pallet boxes are displayed first
  if (palletBoxA.status === PalletBoxStatus.OPEN) {
    if (palletBoxB.status === PalletBoxStatus.OPEN) {
      // Pallet boxes are sorted alphabetically by name
      return palletBoxA.name.localeCompare(palletBoxB.name);
    }
    return -1;
  }
  if (palletBoxB.status !== PalletBoxStatus.OPEN) {
    return palletBoxA.name.localeCompare(palletBoxB.name);
  }
  return 1;
};

const palletBoxCompare = (cutoffMoment: momentTimezone.Moment) =>
  (palletBoxA: PalletBox, palletBoxB: PalletBox) => {
    // Old pallet boxes are displayed first.
    if (cutoffMoment.isAfter(palletBoxA.createdAt)) {
      if (cutoffMoment.isAfter(palletBoxB.createdAt)) {
        return palletBoxCompareByStatus(palletBoxA, palletBoxB);
      }
      return -1;
    }
    if (cutoffMoment.isAfter(palletBoxB.createdAt)) {
      return 1;
    }
    return palletBoxCompareByStatus(palletBoxA, palletBoxB);
  };

interface Props {
  internalShipmentGroup: InternalShipmentGroup;
  widthOffsetPx: number;
  now: momentTimezone.Moment;
  cutoffMoment: momentTimezone.Moment;
}

// tslint:disable-next-line:function-name
export default function ShipmentsTileGroupComponentV2({
  internalShipmentGroup,
  widthOffsetPx,
  cutoffMoment,
}: Props) {
  const [expanded, setExpanded] = React.useState(false);
  return (
    <ContainerDimensions>
      {({ width }) => {
        const maxVisibleTiles = ((width - widthOffsetPx) / tileDisplayWidth) - 1;
        const palletBoxesToDisplay =
          internalShipmentGroup.palletBoxes
            .sort(palletBoxCompare(cutoffMoment))
            .slice(0, expanded ? undefined : maxVisibleTiles);

        let linkText = '';
        let link = null;
        if (internalShipmentGroup.palletBoxes.length > maxVisibleTiles) {
          linkText = expanded ?
            'view less' :
            `+ ${internalShipmentGroup.palletBoxes.length - palletBoxesToDisplay.length} more!`;
          link = (
            <div className={`shipments-tile-link ${FlexBox.FLEX} ${AlignItems.CENTER}`}>
              <Link
                text={linkText}
                className="m-4 shipments-tile text-center"
                onClick={() => setExpanded(!expanded)}
              />
            </div>
          );
        }

        const shipmentsTiles =
          palletBoxesToDisplay
            .map(palletBox => {
              const name = `${palletBox.externalId} ${palletBox.name}`;
              const type = getShipmentsTileComponentType(palletBox);

              return (
                <ShipmentsTileComponentV2
                  key={name}
                  title={name}
                  palletBox={palletBox}
                  iconClassName={type}
                  flagged={cutoffMoment.isAfter(palletBox.createdAt)}
                />
              );
            });

        return (
          <div className={`${FlexBox.FLEX} ${FlexWrap.WRAP}`}>
            {shipmentsTiles}
            {link}
          </div>
        );
      }}
    </ContainerDimensions>
  );
}

function getShipmentsTileComponentType({ status }: PalletBox) {
  return status === PalletBoxStatus.OPEN ?
    Icon.Open :
    Icon.Closed;
}
